/**
 * Ferramentas disponiveis na barra do editor.
 *
 * `mark` aplica/remove um mark no trecho selecionado, `node` envolve o bloco em
 * uma lista, e `action` executa um comando avulso (desfazer, refazer, link). O
 * `i18n` aponta para a chave de traducao — nada de texto fixo em ingles vindo
 * de dentro de node_modules, que era o caso da barra antiga.
 *
 * `group` so afeta o desenho: a barra insere um separador quando o grupo muda,
 * para os nove botoes nao virarem uma fileira indistinta.
 */
export const EDITOR_TOOLS = Object.freeze({
  bold: {
    kind: 'mark',
    mark: 'strong',
    icon: 'text-bold',
    i18n: 'BOLD',
    group: 'format',
  },
  italic: {
    kind: 'mark',
    mark: 'em',
    icon: 'text-italic',
    i18n: 'ITALIC',
    group: 'format',
  },
  strike: {
    kind: 'mark',
    mark: 'strike',
    icon: 'text-strikethrough',
    i18n: 'STRIKE',
    group: 'format',
  },
  // Codigo tem duas formas no WhatsApp, e o editor alterna entre elas sozinho:
  // uma linha vira `codigo` (crase simples, "Codigo"), e no momento em que o
  // texto ganha uma quebra vira um bloco de tres crases ("Monoespacado"). Por
  // isso e uma acao propria, e nao um mark simples: precisa escolher entre o
  // mark `code` e o node `code_block`.
  code: {
    kind: 'action',
    action: 'code',
    icon: 'code',
    i18n: 'CODE',
    group: 'format',
  },
  link: {
    kind: 'action',
    action: 'link',
    icon: 'link',
    i18n: 'LINK',
    group: 'link',
  },
  bulletList: {
    kind: 'node',
    node: 'bullet_list',
    icon: 'text-bullet-list',
    i18n: 'BULLET_LIST',
    group: 'list',
  },
  orderedList: {
    kind: 'node',
    node: 'ordered_list',
    icon: 'text-number-list',
    i18n: 'ORDERED_LIST',
    group: 'list',
  },
  undo: {
    kind: 'action',
    action: 'undo',
    icon: 'arrow-undo',
    i18n: 'UNDO',
    group: 'history',
  },
  redo: {
    kind: 'action',
    action: 'redo',
    icon: 'arrow-redo',
    i18n: 'REDO',
    group: 'history',
  },
  // Grupo proprio tambem: inserir variavel nao e formatacao nem historico, e
  // colada num desses grupos ela viraria mais um icone indistinguivel.
  variable: {
    kind: 'action',
    action: 'variable',
    icon: 'tag',
    i18n: 'VARIABLE',
    group: 'insert',
  },
  // Grupo proprio: e a unica acao destrutiva da barra, e o separador a mantem
  // longe do refazer, que fica logo antes dela.
  clear: {
    kind: 'action',
    action: 'clear',
    icon: 'delete',
    i18n: 'CLEAR',
    group: 'clear',
  },
});

/**
 * Barra padrao de qualquer campo de mensagem, inclusive campanhas e caixas de
 * WhatsApp.
 *
 * Sem sublinhado: o WhatsApp nao tem sublinhado no protocolo, entao um botao
 * aqui prometeria uma formatacao que sumiria na mensagem entregue.
 *
 * Lista e link ficam: a lista markdown chega ao WhatsApp como `- item`, que le
 * bem como lista; e o link, apesar de chegar com a marcacao crua, sempre se
 * comportou assim e serve aos demais canais.
 */
export const MESSAGE_TOOLS = Object.freeze([
  // A ordem segue a do editor anterior; so o monoespacado mudou de lugar,
  // saindo do fim para junto das demais formatacoes — solto la, ele ficava
  // sozinho entre dois separadores.
  'bold',
  'italic',
  'strike',
  'code',
  'link',
  'undo',
  'redo',
  'bulletList',
  'orderedList',
  'clear',
]);

/**
 * Insercao de variavel. Nao e comando do ProseMirror: o botao insere `{{` na
 * posicao do cursor e quem abre a lista e o MESMO plugin de sugestao que ja
 * responde a quem digita o gatilho (issue #569).
 *
 * Fica fora de MESSAGE_TOOLS de proposito -- quem a acrescenta e o proprio
 * RichTextEditor, e so quando `enableVariables` esta ligado. Numa nota privada
 * ela nao aparece, pela mesma razao que o picker de `{{` nao abre la.
 */
export const VARIABLE_TOOL = 'variable';

/**
 * Barra da assinatura: sem listas, que o corpo da assinatura nao comporta.
 *
 * Tambem sem envio de imagem, que a barra anterior
 * (`MESSAGE_SIGNATURE_EDITOR_MENU_OPTIONS`) tinha: a remocao e deliberada, nao
 * um esquecimento da troca de editor.
 */
export const SIGNATURE_TOOLS = Object.freeze([
  'bold',
  'italic',
  'link',
  'undo',
  'redo',
  'clear',
]);
